import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { CalendarIcon, TrendingUp, TrendingDown } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";

interface StockMovementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: "entrada" | "saida";
}

const mockProducts = [
  { id: "1", code: "001", name: "Farinha de Trigo Especial", unit: "kg" },
  { id: "2", code: "002", name: "Pão Francês", unit: "un" },
  { id: "3", code: "003", name: "Bolo de Chocolate", unit: "un" },
  { id: "4", code: "004", name: "Açúcar Refinado", unit: "kg" },
  { id: "5", code: "005", name: "Croissant", unit: "un" },
];

export function StockMovementDialog({ open, onOpenChange, type }: StockMovementDialogProps) {
  const [selectedProduct, setSelectedProduct] = useState("");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [batch, setBatch] = useState("");
  const [expiryDate, setExpiryDate] = useState<Date>();

  const isEntrada = type === "entrada";
  const title = isEntrada ? "Entrada de Estoque" : "Saída de Estoque";
  const description = isEntrada 
    ? "Registre a entrada de produtos no estoque" 
    : "Registre a saída de produtos do estoque";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedProduct || !quantity || !reason) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha todos os campos obrigatórios",
        variant: "destructive",
      });
      return;
    }

    if (isEntrada && (!batch || !expiryDate)) {
      toast({
        title: "Campos obrigatórios",
        description: "Para entradas, lote e validade são obrigatórios",
        variant: "destructive",
      });
      return;
    }

    const product = mockProducts.find(p => p.id === selectedProduct);
    
    toast({
      title: `${isEntrada ? "Entrada" : "Saída"} registrada com sucesso`,
      description: `${quantity} ${product?.unit} de ${product?.name}`,
    });

    // Reset form
    setSelectedProduct("");
    setQuantity("");
    setReason("");
    setBatch("");
    setExpiryDate(undefined);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEntrada ? (
              <TrendingUp className="h-5 w-5 text-success" />
            ) : (
              <TrendingDown className="h-5 w-5 text-destructive" />
            )}
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="product">Produto *</Label>
            <Select value={selectedProduct} onValueChange={setSelectedProduct}>
              <SelectTrigger id="product">
                <SelectValue placeholder="Selecione o produto" />
              </SelectTrigger>
              <SelectContent>
                {mockProducts.map((product) => (
                  <SelectItem key={product.id} value={product.id}>
                    {product.code} - {product.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="quantity">Quantidade *</Label>
            <Input
              id="quantity"
              type="number"
              min="0.01"
              step="0.01"
              placeholder="0"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>

          {isEntrada && (
            <>
              <div className="space-y-2">
                <Label htmlFor="batch">Lote *</Label>
                <Input
                  id="batch"
                  placeholder="Ex: LT2024001"
                  value={batch}
                  onChange={(e) => setBatch(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Data de Validade *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !expiryDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {expiryDate ? format(expiryDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecione"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={expiryDate}
                      onSelect={setExpiryDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="reason">Motivo *</Label>
            <Textarea
              id="reason"
              placeholder={isEntrada ? "Ex: Compra fornecedor, Produção, Devolução" : "Ex: Venda PDV, Produção, Perda"}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" className={isEntrada ? "bg-success hover:bg-success/90" : ""}>
              {isEntrada ? "Registrar Entrada" : "Registrar Saída"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
